import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import ErrorMessage from '../components/ErrorMessage';
import SuccessMessage from '../components/SuccessMessage';
import { Eye, EyeOff, Mail, Lock, ArrowRight } from 'lucide-react';
import { authApi } from '../api/auth';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const { verifyToken } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    // Check if there's a token in URL (magic link callback)
    const token = searchParams.get('token');
    if (token) {
      handleTokenVerification(token);
    }

    // Check if email was just verified
    const verified = searchParams.get('verified');
    if (verified === 'true') {
      setMessage('Email adresiniz onaylandı! Şimdi giriş yapabilirsiniz.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleTokenVerification = async (token: string) => {
    setLoading(true);
    setError('');
    setMessage('');

    try {
      await verifyToken(token);
      setMessage('Giriş başarılı! Yönlendiriliyorsunuz...');
      setTimeout(() => {
        navigate('/dashboard');
      }, 1000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    setError('');
    setMessage('');

    try {
      if (!password) {
        setError('Şifre gerekli');
        setLoading(false);
        return;
      }

      const data = await authApi.loginWithPassword(email, password);
      if (data.success) {
        localStorage.setItem('authToken', data.data.access_token);
        await verifyToken(data.data.access_token);
        setMessage('Giriş başarılı! Yönlendiriliyorsunuz...');
        setTimeout(() => {
          navigate('/dashboard');
        }, 1000);
      } else {
        setError(data.error?.message || 'Giriş başarısız');
      }
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || err.message || 'Giriş başarısız');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-blue-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
            <span className="text-2xl text-white font-bold">⚖️</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Avukasa'ya Hoş Geldiniz</h1>
          <p className="text-gray-600">
            Hesabınıza giriş yaparak devam edin
          </p>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-6 shadow-xl rounded-2xl border border-gray-100">

          {/* Error/Success Messages */}
          {error && (
            <div className="mb-6">
              <ErrorMessage
                message={error}
                onDismiss={() => setError('')}
                onRetry={() => handleSubmit}
              />
            </div>
          )}

          {message && (
            <div className="mb-6">
              <SuccessMessage
                message={message}
                onDismiss={() => setMessage('')}
              />
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Adresi
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:opacity-50"
                  placeholder="ornek@email.com"
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Şifre
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:opacity-50"
                  placeholder="Şifrenizi girin"
                  disabled={loading}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading || !email || !password}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-[1.02] disabled:transform-none"
              >
                <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                  {loading ? (
                    <svg className="h-5 w-5 text-blue-300 group-hover:text-blue-200 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <ArrowRight className="h-5 w-5 text-blue-300 group-hover:text-blue-200" />
                  )}
                </span>
                {loading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
              </button>
            </div>
          </form>

          <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
            <button
              type="button"
              onClick={() => navigate('/forgot-password')}
              className="font-medium text-blue-600 hover:text-blue-500 transition-colors"
            >
              Şifremi unuttum
            </button>
            <button
              type="button"
              onClick={() => navigate('/signup')}
              className="font-medium text-gray-500 hover:text-gray-700 transition-colors"
            >
              Hesabın yok mu? Kaydol
            </button>
          </div>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Nasıl çalışır?</span>
              </div>
            </div>

            <div className="mt-6 text-sm text-gray-600">
              <div className="space-y-2">
                <div className="flex items-start">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-semibold mr-3">1</span>
                  <p>Email adresinizi girin</p>
                </div>
                <div className="flex items-start">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-semibold mr-3">2</span>
                  <p>Size güvenli bir giriş linki göndereceğiz</p>
                </div>
                <div className="flex items-start">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-semibold mr-3">3</span>
                  <p>Email'deki linke tıklayarak giriş yapın</p>
                </div>
              </div>
            </div>

            <div className="mt-8 p-4 bg-blue-50 rounded-xl border border-blue-100 text-sm text-blue-900 space-y-2">
              <p className="font-semibold">Güvenlik & Destek</p>
              <p>Giriş işlemleri TLS/SSL ile şifrelenir ve şifreleriniz sunucuda hash’lenmiş olarak tutulur.</p>
              <p>
                Yardıma ihtiyacınız olursa{' '}
                <a href="mailto:support@avukasa.com" className="font-medium underline hover:text-blue-700">
                  support@avukasa.com
                </a>{' '}
                adresine yazabilirsiniz.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
